import { Color, ScanditIconBuilder, ScanditIconShape, ScanditIconType } from '@scandit/web-datacapture-core'
import type { CapturedLabel } from '@scandit/web-datacapture-label'

const BLACK = Color.fromHex('000000')
const TRANSPARENT = BLACK.withAlpha(0)

function buildIcon(): Promise<HTMLElement> {
  return new ScanditIconBuilder()
    .withIcon(ScanditIconType.ExclamationMark)
    .withIconColor(BLACK)
    .withBackgroundShape(ScanditIconShape.Circle)
    .withBackgroundColor(TRANSPARENT)
    .withBackgroundStrokeColor(BLACK)
    .withBackgroundStrokeWidth(2)
    .withWidth(20)
    .withHeight(20)
    .withIconSize(12)
    .build()
}

const DEFAULT_ARROW_HEIGHT_PX = 7

/**
 * Small `<price-shelf-badge>` web component: a white pill showing an info icon
 * and the price, with a small downward arrow. Use the `price` setter (or
 * `price="…"` attribute) to update the displayed value, and the `arrowHeight`
 * setter to resize the arrow — the same value drives the top and the two side
 * borders of the triangle, so the proportions stay consistent.
 */
export class PriceShelfBadge extends HTMLElement {
  static readonly tag = 'price-shelf-badge'
  static readonly observedAttributes = ['price']

  private readonly textNode: HTMLSpanElement
  private readonly iconHost: HTMLSpanElement
  private _arrowHeight = DEFAULT_ARROW_HEIGHT_PX
  private iconBuilt = false

  constructor() {
    super()
    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = `
        <style>
          :host {
            --arrow-height: ${DEFAULT_ARROW_HEIGHT_PX}px;
            align-items: center;
            background: #fff;
            border-radius: 999px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
            color: #000;
            display: inline-flex;
            font-family: SF Pro Text, -apple-system, system-ui, sans-serif;
            font-size: 1rem;
            font-weight: 600;
            gap: 8px;
            padding: 4px 8px 4px 4px;
            position: relative;
            user-select: none;
            white-space: nowrap;
          }
          :host::after {
            border-left: var(--arrow-height) solid transparent;
            border-right: var(--arrow-height) solid transparent;
            border-top: var(--arrow-height) solid #fff;
            content: '';
            height: 0;
            left: 14px;
            position: absolute;
            top: calc(100% - 2px);
            transform: translateX(-50%);
            width: 0;
          }
          .icon {
            align-items: center;
            display: flex;
            justify-content: center;
          }
        </style>
        <span class="icon" part="icon"></span>
        <span class="text" part="text"></span>
    `
    this.iconHost = shadow.querySelector('.icon') as HTMLSpanElement
    this.textNode = shadow.querySelector('.text') as HTMLSpanElement
  }

  connectedCallback() {
    if (!this.iconBuilt) {
      this.iconBuilt = true
      void buildIcon().then((icon) => this.iconHost.appendChild(icon))
    }
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
    if (name === 'price') {
      this.textNode.textContent = newValue ?? ''
    }
  }

  set price(value: string) {
    this.setAttribute('price', value)
  }

  get price(): string {
    return this.getAttribute('price') ?? ''
  }

  get arrowHeight(): number {
    return this._arrowHeight
  }

  set arrowHeight(value: number) {
    this._arrowHeight = value
    this.style.setProperty('--arrow-height', `${value}px`)
  }

  static register() {
    if (!customElements.get(PriceShelfBadge.tag)) {
      customElements.define(PriceShelfBadge.tag, PriceShelfBadge)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [PriceShelfBadge.tag]: PriceShelfBadge
  }
}

/**
 * Returns the first text-bearing field's value from the captured label, used as
 * the price string on the price-shelf badge.
 */
function extractPriceText(label: CapturedLabel): string {
  for (const field of label.fields) {
    if (field.text) return field.text
  }
  return ''
}

/** Returns the `<price-shelf-badge>` for this label's trackingId, creating one if absent. */
export function createPriceShelfBadge(label: CapturedLabel, cache: Map<number, PriceShelfBadge>): HTMLElement {
  let badge = cache.get(label.trackingId)
  if (!badge) {
    PriceShelfBadge.register()
    badge = document.createElement(PriceShelfBadge.tag) as PriceShelfBadge
    cache.set(label.trackingId, badge)
  }
  badge.price = extractPriceText(label)
  return badge
}
